<template>
  <section
    v-if="visible"
    class="app-block fixed inset-0 z-[1000] overflow-y-auto text-white"
    role="dialog"
    aria-modal="true"
    aria-label="Descargá la app de Julmar"
  >
    <!-- Fondo: degradé oscuro con manchas doradas, igual al banner del catálogo -->
    <div class="app-block__bg" aria-hidden="true">
      <span class="app-block__blob app-block__blob--1" />
      <span class="app-block__blob app-block__blob--2" />
    </div>

    <div
      class="relative z-10 min-h-full flex flex-col items-center px-6 pt-10 pb-8 md:justify-center lg:flex-row lg:gap-12 lg:px-12"
    >
      <!-- Texto + tiendas -->
      <div class="flex flex-col items-center text-center w-full max-w-[420px] md:max-w-[480px] lg:items-start lg:text-left lg:order-1">
        <img src="/images/logo-julmar-2026.svg" alt="Julmar" class="w-[150px] h-auto" />

        <div class="flex items-center gap-2 mt-7 text-[11px] font-semibold uppercase tracking-[0.14em]">
          <span class="app-block__dot" />
          <span class="text-base-yellow">Nueva app disponible</span>
          <span class="text-white/50 normal-case tracking-normal font-normal">· Noviembre 2026</span>
        </div>
        <h1 class="font-montserrat font-extrabold text-[28px] leading-[1.15] mt-2 md:text-[36px]">
          Desde {{ isTablet ? 'la tablet' : 'el celular' }}, Julmar se usa con la <span class="text-base-yellow">nueva app</span>
        </h1>
        <p class="text-[14px] leading-snug text-white/75 mt-3">
          Rediseño total, mucho más rápida y con nueva tecnología. Catálogo, carrito y novedades con integración nativa en tu {{ isTablet ? 'tablet' : 'teléfono' }}.
        </p>

        <!-- Mockups en celular: entre el texto y los botones -->
        <div class="app-block__phones lg:hidden" aria-hidden="true">
          <div
            v-for="(phone, i) in phones"
            :key="phone.src"
            class="app-block__phone"
            :class="`is-${phone.pos}`"
            :style="{ animationDelay: `${i * -1.6}s` }"
          >
            <img :src="phone.src" alt="" draggable="false" />
          </div>
        </div>

        <div class="flex flex-col gap-3 w-full mt-2 sm:flex-row lg:mt-8">
          <a
            v-for="store in stores"
            :key="store.id"
            :href="store.url"
            target="_blank"
            rel="noopener"
            class="app-block__store"
            :aria-label="store.label"
          >
            <svg v-if="store.id === 'ios'" viewBox="0 0 24 24" class="w-[26px] h-[26px]" fill="currentColor" aria-hidden="true">
              <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
            </svg>
            <svg v-else viewBox="0 0 24 24" class="w-[24px] h-[24px]" aria-hidden="true">
              <path fill="#00D7FE" d="M3.6 1.8 13.4 12 3.6 22.2c-.3-.2-.5-.6-.5-1.1V2.9c0-.5.2-.9.5-1.1z" />
              <path fill="#00F076" d="M16.8 8.6 13.4 12 3.6 1.8c.3-.2.8-.2 1.3 0l11.9 6.8z" />
              <path fill="#FFC900" d="M20.4 10.6c.9.5.9 1.9 0 2.4l-3.6 2.1-3.4-3.1 3.4-3.4 3.6 2z" />
              <path fill="#F8323B" d="M16.8 15.4 4.9 22.2c-.5.3-1 .3-1.3 0L13.4 12l3.4 3.4z" />
            </svg>
            <span class="flex flex-col leading-none text-left">
              <span class="text-[10px] font-normal">{{ store.kicker }}</span>
              <span class="text-[18px] font-semibold tracking-tight mt-0.5">{{ store.name }}</span>
            </span>
          </a>
        </div>

        <ol class="app-block__steps">
          <li><span>1</span>Descargá la app desde tu tienda.</li>
          <li><span>2</span>Ingresá con tu mismo usuario y clave.</li>
          <li><span>3</span>Pedí la habilitación del dispositivo (se hace una sola vez).</li>
        </ol>

        <p class="text-[12px] text-white/50 mt-6">
          Desde una computadora podés seguir usando la web como siempre.
        </p>
      </div>

      <!-- Mockups en tablet: columna derecha, más grandes -->
      <div class="app-block__phones app-block__phones--side hidden lg:block lg:order-2" aria-hidden="true">
        <div
          v-for="(phone, i) in phones"
          :key="phone.src"
          class="app-block__phone"
          :class="`is-${phone.pos}`"
          :style="{ animationDelay: `${i * -1.6}s` }"
        >
          <img :src="phone.src" alt="" draggable="false" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// En celulares y tablets la web de clientes queda bloqueada: solo se muestra
// esta pantalla con los links a las tiendas (la app 10.0.0 sale en
// noviembre 2026). Las rutas de admin no se bloquean (lo decide App.vue).
// Para probar la web en el celular durante el desarrollo:
// VUE_APP_IGNORE_IS_MOBILE=true.
const APP_STORE = {
  id: 'ios',
  url: 'https://apps.apple.com/ar/app/julmar/id1572755762',
  label: 'Descargar en el App Store',
  kicker: 'Consíguelo en el',
  name: 'App Store'
}
const GOOGLE_PLAY = {
  id: 'android',
  url: 'https://play.google.com/store/apps/details?id=com.aureadevo.julmar',
  label: 'Descargar en Google Play',
  kicker: 'DISPONIBLE EN',
  name: 'Google Play'
}

function detectarDispositivo() {
  const ua = navigator.userAgent || ''
  // iPadOS se presenta como Mac de escritorio: lo delata el touch
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
  const ios = /iPhone|iPad|iPod/.test(ua) || iPadOS
  const android = /Android/.test(ua)
  const tablet = /iPad/.test(ua) || iPadOS || (android && !/Mobile/.test(ua))
  const movil = ios || android || /Mobi|BlackBerry|Windows Phone/i.test(ua)
  return { movil, tablet, ios, android }
}

export default {
  name: 'MobileAppNotice',
  data() {
    return {
      visible: false,
      isTablet: false,
      stores: [APP_STORE, GOOGLE_PLAY],
      phones: [
        { src: '/images/app-banner/carrito.webp', pos: 'left' },
        { src: '/images/app-banner/producto.webp', pos: 'right' },
        { src: '/images/app-banner/catalogo.webp', pos: 'center' }
      ]
    }
  },
  created() {
    if (process.env.VUE_APP_IGNORE_IS_MOBILE === 'true') return
    const d = detectarDispositivo()
    if (!d.movil) return
    this.isTablet = d.tablet
    // Primero la tienda del sistema del usuario
    if (d.android) this.stores = [GOOGLE_PLAY, APP_STORE]
    this.visible = true
  },
  watch: {
    // Sin scroll de la web de abajo mientras está el bloqueo
    visible: {
      immediate: true,
      handler(v) {
        document.documentElement.style.overflow = v ? 'hidden' : ''
      }
    }
  },
  beforeUnmount() {
    document.documentElement.style.overflow = ''
  }
}
</script>

<style scoped>
.app-block {
  background: linear-gradient(160deg, #120c05 0%, #23170a 50%, #3a250b 100%);
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
}

.app-block__bg {
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.app-block__blob {
  position: absolute;
  border-radius: 9999px;
  filter: blur(70px);
  animation: blob-drift 14s ease-in-out infinite alternate;
}
.app-block__blob--1 {
  width: 80vw;
  height: 80vw;
  top: 18%;
  left: 30%;
  background: rgba(242, 168, 64, 0.32);
}
.app-block__blob--2 {
  width: 60vw;
  height: 60vw;
  bottom: -20%;
  left: -20%;
  background: rgba(242, 168, 64, 0.16);
  animation-delay: -7s;
}

.app-block__dot {
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: #f2a840;
  box-shadow: 0 0 0 0 rgba(242, 168, 64, 0.6);
  animation: dot-pulse 2s ease-out infinite;
}

/* Tres teléfonos en abanico: el del centro adelante */
.app-block__phones {
  position: relative;
  width: 300px;
  height: 370px;
  margin: 22px auto 0;
}
.app-block__phones--side {
  width: 380px;
  height: 480px;
  margin: 0;
  flex-shrink: 0;
}
.app-block__phone {
  position: absolute;
  bottom: 0;
  width: 132px;
  filter: drop-shadow(0 16px 20px rgba(0, 0, 0, 0.55));
  animation: phone-float 5.5s ease-in-out infinite;
}
.app-block__phones--side .app-block__phone {
  width: 172px;
}
.app-block__phone img {
  display: block;
  width: 100%;
  height: auto;
}
.app-block__phone.is-center {
  left: 50%;
  margin-left: -66px;
  z-index: 2;
}
.app-block__phones--side .app-block__phone.is-center {
  margin-left: -86px;
}
.app-block__phone.is-left {
  left: 4%;
  bottom: 18px;
  --rot: -10deg;
  transform: rotate(-10deg) scale(0.88);
  opacity: 0.9;
}
.app-block__phone.is-right {
  right: 4%;
  bottom: 18px;
  --rot: 10deg;
  transform: rotate(10deg) scale(0.88);
  opacity: 0.9;
}

.app-block__store {
  /* basis auto: con flex 1 (basis 0) en columna el botón se achicaba y el texto quedaba afuera */
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 56px;
  padding: 0 18px;
  border-radius: 12px;
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  text-decoration: none;
  transition: transform 0.15s ease, border-color 0.15s ease;
}
.app-block__store:active {
  transform: scale(0.97);
  border-color: #f2a840;
}

.app-block__steps {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-top: 24px;
  padding: 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-align: left;
  font-size: 13.5px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.85);
}
.app-block__steps li {
  display: flex;
  align-items: center;
  gap: 10px;
}
.app-block__steps span {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  background: #f2a840;
  color: #1f2024;
  font-weight: 800;
  font-size: 12px;
}

@keyframes phone-float {
  0%, 100% { translate: 0 0; }
  50% { translate: 0 -8px; }
}
@keyframes blob-drift {
  from { transform: translate(0, 0); }
  to { transform: translate(-12%, 8%); }
}
@keyframes dot-pulse {
  0% { box-shadow: 0 0 0 0 rgba(242, 168, 64, 0.6); }
  100% { box-shadow: 0 0 0 9px rgba(242, 168, 64, 0); }
}

@media (prefers-reduced-motion: reduce) {
  .app-block__phone,
  .app-block__blob,
  .app-block__dot {
    animation: none;
  }
}
</style>
